// A week's slots — which day holds which script, and which days are owed —
// computed from rows, with no store behind it.
//
// PURE, AND NOT "use client", ON PURPOSE. The editor fills a week's slots from
// its client store; the server's submit notice (lib/server/submit-notice.ts)
// has to count the SAME slots to decide whether the creator is finished, and a
// server that counts differently from the page is the bug this file was split
// out to end: the server used
// to count only the draft rows that existed, so an unwritten day owed nothing
// and a creator who submitted one script out of five looked done. Both sides
// now call weekSlotsFrom, and both ask pendingOn what is left.
//
// Imports here must stay pure too — types, and modules with no store in them.

import type { ScriptEntry } from "./ironside-script-data";
import type { ScriptStage, FootageStage } from "./stage-model";
import type { Actor, Ladder } from "./submit-notice-types";
import { isBlankFields, fieldsFromEntry } from "./ironside-script-fields";

/**
 * One publish day of a week's plan. `days[i]` is day index `i + 1`.
 *
 * `date` IS THE DAY, and the reason the array has an order at all. It used to be
 * derived — day i's date WAS `publishDays(postDate)[i]`, so position and date
 * were the same fact and neither could be edited alone. Now the date is stored
 * per day and the order is derived from it (ascending), which is what lets an AM
 * put two videos on one date, or send one into the week before, without dragging
 * anything.
 *
 * `date: null | undefined` means NOT MATERIALIZED — every week written before
 * this change, and every week whose post date nobody has re-saved. Those fall
 * back to `publishDays(postDate)[i]`, which is exactly what they rendered
 * before, so no migration is needed and no week changes appearance until it is
 * edited. `effectiveDates` in lib/video-dates.ts is that fallback, in one place.
 *
 * EMPTY IS NOT SKIPPED, and the two fields say so independently. `scriptId:
 * null` means nobody has written this video *yet* — it still owes the week a
 * video and still counts toward its denominator. `skipped: true` means no video
 * will ever go out this day, on purpose — it drops out of the denominator. A
 * skipped day always holds no script, so the two are never both truthy.
 *
 * NOTHING WRITES `skipped` ANY MORE. Skip-day was an edit-mode control, and edit
 * mode is gone; the field stays because rows written before that still carry
 * it and both the Videos page and the editor still READ it.
 */
export type DayPlan = {
  scriptId: string | null;
  skipped: boolean;
  date?: string | null;
};

/** A batch's publish plan — one entry per day the week owes. Its length is the
 *  creator's commitment when the week was minted. */
export type WeekPlan = {
  days: DayPlan[];
};

// `skipped` comes from a stored plan and is false for
// every planless week. A skipped day owes no video: weekCompleteness and
// pendingOn drop it from their counts.
export type WeekSlot = { videoNum: number; entry: ScriptEntry | null; skipped: boolean };

/**
 * The slots of a week, filled from the scripts you hand it, null where nothing
 * is written yet.
 *
 * Takes scripts rather than a (creator, week) so a caller that has ALREADY
 * narrowed them — the Videos page, which scopes by persona and by the brand
 * pill — can fill its slots without widening back out. weekSlotsFor does the
 * lookup for callers that have not.
 *
 * A week of nulls is not a week: only pass in the scripts of a week that has at
 * least one.
 *
 * ⚠️ HOW MANY SLOTS IS THE PLAN'S BUSINESS NOW (since 2026-09-11). A week
 * created since then carries a stored plan whose LENGTH is the creator's
 * commitment, frozen at the moment the week was minted; the constant below is
 * what a week with no plan at all has, which is every week written before dates
 * were stored and is five, as it always was. The plan was already the truth
 * about ORDER — this is the same fact, counted.
 */
export const SLOTS_PER_WEEK = 5;

/**
 * With a `plan`, the plan is the truth: day `i` holds the script it names (looked
 * up by id in the scripts you pass) and carries its own skip flag. Without one —
 * every week today — it derives the legacy plan: day `i` ← the script whose
 * `videoNum` is `i`, no skips. The two paths agree for a week whose plan is
 * exactly its `videoNum`s, which is why an unplanned week can fall through to
 * the cheaper branch and read identically.
 */
/** Script ids already warned about — see adoptStranded. Session-scoped; a reload
 *  re-warns, which is what you want when someone reports the page looks wrong. */
const warnedStranded = new Set<string>();

/**
 * Put back any script the plan forgot. Mutates `slots`, in place, after the plan
 * has had first refusal on every day.
 *
 * THE PLAN IS STILL THE TRUTH ABOUT ORDER. A script the plan PLACES is placed
 * where the plan says, videoNum be damned — that is the whole reason order lives
 * outside the script row, and weekSlotsFrom's plan branch above is unchanged.
 * This is about a script the plan does not mention ANYWHERE: it used to resolve
 * to nothing, and a slot resolving to nothing is indistinguishable from a slot
 * nobody has written. Week 18 (2026-07-17) is what that costs — a plan staged
 * empty and never discarded held five written scripts off the page for a day
 * while its creator, whose canvas renders from local state, watched his own text
 * and could not submit it. There is no correct week in which a written script
 * belongs to no day.
 *
 * IN MEMORY ONLY, on purpose. Reads do not write. The stored plan keeps whatever
 * it says and an operator repairs it (tools/audit-week-plans.js finds them); this
 * only refuses to render a lie in the meantime. So the warning is the point —
 * adoption means a plan is wrong somewhere and nobody has fixed it yet.
 *
 * ONCE PER SCRIPT PER SESSION. This runs on every render of the Videos page and
 * the editor; a warning per adoption per render is a spammed console, and a
 * console nobody can read is the condition that let Week 18 last a day.
 *
 * A SKIPPED DAY IS NOT A FREE DAY. No video ever goes out that day, so a script
 * parked there could never publish, and weekCompleteness drops skipped days from
 * its denominator — filling one would drag the script back into a count it was
 * deliberately excluded from.
 */
function adoptStranded(slots: WeekSlot[], scripts: ScriptEntry[]): void {
  const placed = new Set(slots.map((s) => s.entry?.id).filter(Boolean));
  const isFree = (s: WeekSlot) => s.entry === null && !s.skipped;

  const claim = (script: ScriptEntry, target: WeekSlot) => {
    target.entry = script;
    placed.add(script.id);
    if (!warnedStranded.has(script.id)) {
      warnedStranded.add(script.id);
      console.warn(
        `[week-slots] plan does not reference ${script.id} (week ${script.week}, video ${script.videoNum}) — adopted into day ${target.videoNum}. The stored plan needs repair.`,
      );
    }
  };

  // WRITTEN WORK OUTRANKS AN EMPTY ROW, and days are handed out in two passes.
  // Both rules exist because of what the real Week 18 rows do to the naive
  // version — take each stranded script in turn and give it its own day or the
  // first free one:
  //
  //   D-6 (blank, video 1)  D-7 (written, video 1)  D-8..D-11 (written, 2..5)
  //   → day 1 = D-6, and D-7 pushes D-8 to day 3, D-9 to day 4, D-10 to day 5,
  //     and D-11 off the end of the week entirely.
  //
  // A blank duplicate took the day, every script slid off its own number, one
  // real script (graded 82) disappeared, and the blank one still counted as
  // unwritten — so Submit STAYED disabled. The rescue reproduced the bug.
  //
  // So: everything that has words gets first claim on its own day; blanks pick
  // over what is left. A row nobody has typed into has nothing to lose by losing.
  const isWritten = (s: ScriptEntry) => !isBlankFields(fieldsFromEntry(s));
  const stranded = scripts.filter((s) => !placed.has(s.id));
  const written = stranded.filter(isWritten);
  const blank = stranded.filter((s) => !isWritten(s));

  // Pass 1 — own day only. Never take a day that belongs to someone still to be
  // asked; that is what turned one collision into a five-slot cascade.
  const homeless: ScriptEntry[] = [];
  for (const script of [...written, ...blank]) {
    const own = slots[script.videoNum - 1];
    if (own && isFree(own)) claim(script, own);
    else homeless.push(script);
  }

  // Pass 2 — anywhere left, in the same order, so a script whose own day was
  // taken still reaches the page rather than vanishing.
  for (const script of homeless) {
    const target = slots.find(isFree);
    if (target) {
      claim(script, target);
      continue;
    }

    // NOWHERE TO PUT IT. A week has five days and a skipped one has fewer, so
    // more scripts than days is representable — the Videos page calls the excess
    // "overflow" and gives it a place to sit; this function has no such thing and
    // simply cannot show it.
    //
    // A BLANK one going unplaced is the design working: it is the row addWeek
    // minted and nobody typed into, and losing it is the whole point of ranking
    // words above emptiness. Silence is right for that.
    //
    // A WRITTEN one going unplaced is somebody's work vanishing off a page with
    // no explanation — the exact thing this function exists to stop, happening
    // inside the function that stops it. It got a bare `continue` and said
    // nothing, which is how Week 18 lasted a day. Found by driving a poisoned
    // week with a skipped day in a browser (2026-07-17): four scripts announced
    // their adoption and the fifth disappeared without a word.
    if (isWritten(script) && !warnedStranded.has(script.id)) {
      warnedStranded.add(script.id);
      console.error(
        `[week-slots] ${script.id} (week ${script.week}, video ${script.videoNum}) has content and NO day to sit on — the week has more scripts than usable days. It is not being rendered. Repair the stored plan.`,
      );
    }
  }
}

export function weekSlotsFrom(scripts: ScriptEntry[], plan?: WeekPlan): WeekSlot[] {
  if (plan) {
    // ⚠️ THE PLAN'S OWN LENGTH, and never less than one. A stored plan of zero
    // days is not a week that owes nothing — it is a row this app did not write,
    // and drawing no slots for it would hide every script in the week behind a
    // heading with nothing under it. adoptStranded below is what puts a script
    // the plan forgot back on the page; it needs somewhere to put it.
    const length = Math.max(1, plan.days.length);
    const slots: WeekSlot[] = Array.from({ length }, (_, i) => {
      const day = plan.days[i];
      return {
        videoNum: i + 1,
        entry: day?.scriptId ? scripts.find((s) => s.id === day.scriptId) ?? null : null,
        skipped: day?.skipped ?? false,
      };
    });
    adoptStranded(slots, scripts);
    return slots;
  }
  return Array.from({ length: SLOTS_PER_WEEK }, (_, i) => i + 1).map((n) => ({
    videoNum: n,
    entry: scripts.find((s) => s.videoNum === n) ?? null,
    skipped: false,
  }));
}

// ─── What is left on a ladder ────────────────────────────────────────────────

/**
 * One slot, as the "is anyone finished?" question sees it.
 *
 * `script: null` IS AN UNWRITTEN SLOT — a day the week owes that has no draft
 * row at all. It is not the same as a draft at `to-be-submitted`, and the
 * difference matters to a reader ("2 still to be written"), but to this rule
 * they are the same fact: the creator still owes that script.
 *
 * `footage` has no null, because it never needed one: a slot whose script is
 * not approved yet reads `to-be-uploaded`, the default the client's
 * footageStage gives every id, and that is exactly right — its video is owed.
 */
export interface LadderSlot {
  skipped: boolean;
  script: ScriptStage | null;
  footage: FootageStage;
}

/**
 * The slots where `actor` still has something to do on `ladder`. THE WHOLE RULE
 * behind "your AM gets one Slack message when the last one is in", in one list.
 *
 * ONE FUNCTION, TWO CALLERS, and that is the reason it is here. The server asks
 * it whether to send the message; the creator's status bar and submit pop-up
 * ask it how many are left. If those two ever counted differently, the
 * page would say "1 more script to submit" over a message that had already
 * gone, or the other way round.
 *
 * A SKIPPED DAY OWES NOTHING, so it is dropped here rather than by each caller:
 * no video goes out that day, and a rule that let each caller remember that
 * would be a rule one of them forgets.
 *
 * PER LADDER, for the creator too. The creator's ladder used to be derived
 * — "has every script been approved?" — because filming waited for the whole
 * week, so only one ladder could ever have creator work on it. That lock is
 * gone (each video films as soon as its own script is approved), so a
 * creator can owe scripts and videos at once; the question is about the ladder
 * they just acted on.
 *
 *  - script: unwritten, `to-be-submitted`, `edits-requested`.
 *  - video: `to-be-uploaded`, `edits-requested` — including a slot whose script
 *    is not approved yet, because its video is still owed.
 *
 * The BRAND's rung is the same shape on both ladders: whatever is in front of
 * them.
 */
export function pendingOn<S extends LadderSlot>(actor: Actor, ladder: Ladder, slots: S[]): S[] {
  const active = slots.filter((s) => !s.skipped);
  if (actor === "brand") {
    return active.filter((s) =>
      ladder === "video" ? s.footage === "awaiting-brand-review" : s.script === "awaiting-brand-review",
    );
  }
  return ladder === "video"
    ? active.filter((s) => s.footage === "to-be-uploaded" || s.footage === "edits-requested")
    : active.filter((s) => s.script === null || s.script === "to-be-submitted" || s.script === "edits-requested");
}
